import { Component, DestroyRef, inject, signal, type Signal, type WritableSignal } from "@angular/core";

import { AUTH_PROVIDER } from "../../../core/auth/auth-provider.token";
import { TENANT_CONTEXT } from "../../../core/auth/tenant-context.token";
import { BrandingService, DAVACO_BRANDING } from "../../../core/services/branding.service";
import { AuthLayoutComponent } from "../../../shared/components/auth-layout/auth-layout.component";
import { MicrosoftMarkComponent } from "../../../shared/components/microsoft-mark/microsoft-mark.component";
import { BrandLogoComponent } from "../../../shared/components/brand-logo/brand-logo.component";

import type { IPortalBranding } from "../../../core/models/i-portal-branding";
import type { ITenantContext } from "../../../core/auth/i-tenant-context";

/**
 * "Your session has expired" — the standalone route.
 *
 * NOT on board 7. Board 6a draws a session-expired MODAL, which is the in-app
 * case: a session that lapses while someone is working, over the page they were
 * on. This is the other one — arriving cold at a URL with nothing behind it —
 * and it is the third full-page screen in the same family as the door (7e) and
 * the no-access screen (7f). Left on its old centred-text styling it would have
 * looked orphaned beside the two redesigned ones.
 *
 * The copy deliberately does NOT quote a session length. Board 6a's modal says
 * "8 hours or 30 minutes of inactivity"; nothing in this portal configures
 * either number, and the two doors expire on different providers' defaults, so
 * printing it would state a policy that is not this system's and would be wrong
 * for at least one door.
 */
@Component({
  selector: "app-session-expired",
  standalone: true,
  imports: [AuthLayoutComponent, MicrosoftMarkComponent, BrandLogoComponent],
  templateUrl: "./session-expired.component.html",
  styleUrl: "./session-expired.component.scss",
})
export class SessionExpiredComponent {

  private readonly provider = inject(AUTH_PROVIDER);

  private readonly brandingService = inject(BrandingService);

  private readonly tenant: ITenantContext = inject(TENANT_CONTEXT);

  public readonly branding: Signal<IPortalBranding> = this.brandingService.branding;

  /** True on the apex host: the sign-in hands off to Microsoft, not Auth0. */
  public readonly isStaffDoor: boolean = this.tenant.kind === "entra";

  public readonly failed: WritableSignal<boolean> = signal<boolean>(false);

  public readonly pending: WritableSignal<boolean> = signal<boolean>(false);

  public constructor() {
    // A customer host keeps the customer's branding: the visitor is looking at
    // that customer's address, and signs back in through its Auth0 door.
    if (!this.isStaffDoor) {
      return;
    }

    // The apex has the same stale-acting-org exposure as /no-access (#661).
    // BrandingService.load() resolves `readActingOrgSlug() ?? tenant.organization`
    // before authentication, so an employee whose last session was viewing as a
    // customer would see that customer's colors beside a Microsoft sign-in to
    // the Davaco staff portal. Borrowed, so leaving puts the session's back.
    this.brandingService.borrow(DAVACO_BRANDING, inject(DestroyRef));
  }

  /**
   * Same shape as the door's hand-off: guard against a double click, and
   * re-enable on failure so a visitor is not stranded on a screen whose only
   * action has stopped working.
   */
  public async signInAgain(): Promise<void> {
    this.failed.set(false);
    this.pending.set(true);

    try {
      await this.provider.login();
    } catch {
      this.failed.set(true);
    } finally {
      this.pending.set(false);
    }
  }
}
